import {
  IMPORT_OUTCOMES,
  IMPORT_REPORT_ENTITIES,
  formatDate,
  formatInteger,
  formatTime,
  importReportEntityLabels,
  totalOutcomeCounts,
  type ExternalProperty,
  type ImportOutcome,
  type ImportOutcomeCounts,
  type ImportReportCounts,
  type ImportRunStatus,
  type ImportRunSummary,
  type PropertySelection,
  type PropertySelectionMode,
} from '@brimo/shared';
import type { Tone } from '../../../properties/types';

/**
 * Pure helpers of Einstellungen › Organisation › Integrationen (D-189/D-190): the property selection, the
 * run list texts and the report tables. The page renders what these return.
 */

/** Short column heads of the counts table (Neu, Geändert, …) in report order. */
export const outcomeColumns: { outcome: ImportOutcome; label: string }[] = [
  { outcome: 'new', label: 'Neu' },
  { outcome: 'changed', label: 'Geändert' },
  { outcome: 'unchanged', label: 'Unverändert' },
  { outcome: 'gone', label: 'Entfallen' },
  { outcome: 'conflict', label: 'Konflikt' },
  { outcome: 'failed', label: 'Fehler' },
  { outcome: 'skipped', label: 'Übersprungen' },
];

export interface CountsRow {
  key: string;
  label: string;
  counts: ImportOutcomeCounts;
  total: boolean;
}

/** One row per entity (in report order) plus the „Gesamt" row. */
export function countsRows(counts: ImportReportCounts): CountsRow[] {
  const rows: CountsRow[] = IMPORT_REPORT_ENTITIES.map((entity) => ({
    key: entity,
    label: importReportEntityLabels[entity],
    counts: counts[entity],
    total: false,
  }));
  rows.push({ key: 'total', label: 'Gesamt', counts: totalOutcomeCounts(counts), total: true });
  return rows;
}

export function isRunActive(status: ImportRunStatus): boolean {
  return status === 'queued' || status === 'running';
}

export const runStatusTones: Record<ImportRunStatus, Tone> = {
  queued: 'neutral',
  running: 'info',
  succeeded: 'success',
  failed: 'danger',
};

/** „12.10.2026, 14:05" */
export function dateTimeLabel(iso: string | null): string {
  return iso ? `${formatDate(iso)}, ${formatTime(iso)}` : '—';
}

/** „2 min 05 s", „48 s", „—" while not started; running runs count up to `now`. */
export function durationLabel(run: Pick<ImportRunSummary, 'startedAt' | 'finishedAt'>, now: number): string {
  if (!run.startedAt) return '—';
  const end = run.finishedAt ? Date.parse(run.finishedAt) : now;
  const seconds = Math.max(0, Math.round((end - Date.parse(run.startedAt)) / 1000));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return `${minutes} min ${String(rest).padStart(2, '0')} s`;
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;
}

/** „automatisch" for the hourly schedule, else the starter's name. */
export function startedByLabel(run: Pick<ImportRunSummary, 'startedBy'>): string {
  return run.startedBy ? run.startedBy.name : 'automatisch';
}

/** „12 neu · 3 geändert · 1 Konflikt" — non-zero outcomes of the totals, „keine Änderungen" otherwise. */
export function totalsLine(totals: ImportOutcomeCounts | null): string {
  if (!totals) return '—';
  const words: Record<ImportOutcome, [string, string]> = {
    new: ['neu', 'neu'],
    changed: ['geändert', 'geändert'],
    unchanged: ['unverändert', 'unverändert'],
    gone: ['entfallen', 'entfallen'],
    conflict: ['Konflikt', 'Konflikte'],
    failed: ['Fehler', 'Fehler'],
    skipped: ['übersprungen', 'übersprungen'],
  };
  const parts = IMPORT_OUTCOMES.filter((outcome) => outcome !== 'unchanged' && totals[outcome] > 0).map(
    (outcome) => `${formatInteger(totals[outcome])} ${words[outcome][totals[outcome] === 1 ? 0 : 1]}`,
  );
  return parts.length > 0 ? parts.join(' · ') : 'keine Änderungen';
}

/** „42 von 180 · Einheiten" while a run is in progress. */
export function progressLine(progress: ImportRunSummary['progress']): string | null {
  if (!progress || progress.total === 0) return null;
  return `${formatInteger(progress.processed)} von ${formatInteger(progress.total)}`;
}

/** „1,4 GB", „820 MB", „12 KB" (decimal units like the file rows). */
export function bytesLabel(bytes: number): string {
  const units: [number, string][] = [
    [1e9, 'GB'],
    [1e6, 'MB'],
    [1e3, 'KB'],
  ];
  for (const [size, unit] of units) {
    if (bytes >= size) {
      const value = bytes / size;
      return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: value < 10 ? 1 : 0 }).format(value)} ${unit}`;
    }
  }
  return `${formatInteger(bytes)} B`;
}

// --- property selection ---------------------------------------------------------------------------------

/** The ids that are imported according to the server (`included`). */
export function includedIds(items: readonly ExternalProperty[]): Set<string> {
  return new Set(items.filter((item) => item.included).map((item) => item.externalId));
}

/**
 * The selection to store for the checked objects. `exclude` (default): every TeamProQ object is imported
 * except the unchecked ones — new objects in TeamProQ come along automatically. `include`: only the checked
 * objects — new objects wait until somebody checks them.
 */
export function selectionFor(
  mode: PropertySelectionMode,
  items: readonly ExternalProperty[],
  checked: ReadonlySet<string>,
): PropertySelection {
  const ids = items.map((item) => item.externalId);
  const externalIds =
    mode === 'include' ? ids.filter((id) => checked.has(id)) : ids.filter((id) => !checked.has(id));
  return { mode, externalIds: [...externalIds].sort() };
}

/** True when the stored selection differs from the checked state or the mode. */
export function selectionChanged(
  stored: PropertySelection,
  next: PropertySelection,
  items: readonly ExternalProperty[],
): boolean {
  if (stored.mode !== next.mode) return true;
  const visible = new Set(items.map((item) => item.externalId));
  const a = stored.externalIds.filter((id) => visible.has(id)).sort();
  const b = [...next.externalIds].sort();
  return a.length !== b.length || a.some((id, index) => id !== b[index]);
}
